import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from './apiConfig'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPhone, faMapMarkerAlt, faCamera, faChevronLeft, 
    faStar, faCalendarAlt, faListAlt, faChevronRight, faWallet, faBell, faSpinner
} from '@fortawesome/free-solid-svg-icons';

const Profile = ({ user, setUser, onLogout }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('orders');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({ 
        full_name: '', email: '', phone: '', address: '', phone1: '', phone2: '' 
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const token = localStorage.getItem('token');
    const gold = '#c5a059';

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        
        if (user) {
            setFormData({ 
                full_name: user.full_name || '', 
                email: user.email || '', 
                phone: user.phone || '', 
                address: user.address || '',
                phone1: user.phone1 || '',
                phone2: user.phone2 || ''
            });
            fetchOrders();
        }
        return () => window.removeEventListener('resize', handleResize);
    }, [user]);

   const fetchOrders = async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken || !user?.id) return;

    setLoadingOrders(true);
    try {
        const res = await axios.get(`${API_BASE}/api/user-orders/${user.id}`, {
            headers: { 
                'Authorization': `Bearer ${currentToken}`,
                'ngrok-skip-browser-warning': 'true' // Եթե ngrok ես օգտագործում
            }
        });
        
        // ✅ Ստուգում ենք՝ արդյոք res.data-ն զանգված է
        if (Array.isArray(res.data)) {
            setOrders(res.data);
        } else if (res.data && Array.isArray(res.data.orders)) {
            // Եթե տվյալները օբյեկտի մեջ են (օրինակ՝ res.data.orders)
            setOrders(res.data.orders);
        } else {
            setOrders([]); // Եթե ոչ մի տարբերակը չեղավ
        }
    } catch (err) {
        console.error("Orders Fetch Error:", err);
        setOrders([]); // Սխալի դեպքում դատարկ զանգված, որ չկրաշ լինի
    } finally {
        setLoadingOrders(false);
    }
};
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleUpdateInfo = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const storageKey = 'liber_user'; 
            const userData = JSON.parse(localStorage.getItem(storageKey));

            if (!userData || !userData.id) {
                alert(t('missing_data'));
                return;
            }

            const data = new FormData();
            data.append('full_name', formData.full_name);
            data.append('phone', formData.phone);
            data.append('phone1', formData.phone1);
            data.append('phone2', formData.phone2);
            data.append('address', formData.address);

            if (selectedFile) {
                data.append('avatar', selectedFile); 
            }

            const res = await fetch(`${API_BASE}/api/auth/update/${userData.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                },
                body: data
            });

            const result = await res.json();

            if (result.success) {
                alert(t('update_success'));
                localStorage.setItem(storageKey, JSON.stringify(result.user));
                setUser(result.user);
                setSelectedFile(null);
                setPreviewUrl(null);
            } else {
                alert(t('update_error') + ": " + (result.error || result.message));
            }
        } catch (err) {
            alert(t('conn_error'));
        } finally {
            setLoading(false);
        }
    };

    const updateNotify = async (key, val) => {
        const newSettings = { ...user.notification_settings, [key]: val };
        try {
            await axios.put(`${API_BASE}/api/users/update/${user.id}`, 
                { notification_settings: newSettings },
                { headers: { 'Authorization': `Bearer ${token}` }}
            );
            setUser({ ...user, notification_settings: newSettings });
        } catch (err) { console.log(err); }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'new': return '#4da3ff';
            case 'pending_finish': return '#ffcc00';
            case 'finished': return '#00ff88';
            case 'cancelled': return '#ff4444';
            default: return gold;
        }
    };

    if (!user) return null;

    return (
        <div style={isMobile ? s.fullWrapper : s.desktopOverlay}>
            <Helmet><title>{t('title')} | LIBER</title></Helmet>

            <div style={isMobile ? s.mobileContainer : s.desktopModal}>
                <div style={s.header}>
                    <button onClick={() => navigate('/')} style={s.backBtn}><FontAwesomeIcon icon={faChevronLeft} /></button>
                    <div style={s.headerTitle}>{t('title')}</div>
                    <div style={{color: '#555'}}><FontAwesomeIcon icon={faBell} /></div>
                </div>

                <div style={s.scrollArea}>
                    <div style={s.hero}>
                        <div style={s.avatarWrapper}>
   <img 
    src={
        previewUrl || 
        (user.profile_pic 
            ? `${API_BASE}/${user.profile_pic.replace(/^\//, '')}` // Հանեցինք getTime()-ը արագության համար
            : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
        )
    } 
    style={s.avatar} 
    alt="User Profile" 
    loading="eager" // Պրոֆիլի նկարը սովորաբար կարևոր է, թող բեռնվի անմիջապես
    decoding="async"
    onError={(e) => {
        e.target.onerror = null; 
        e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
    }} 
/>
                            <label style={s.photoEditBtn}>
                                <FontAwesomeIcon icon={faCamera} />
                                <input type="file" hidden onChange={handleFileChange} accept="image/*" />
                            </label>
                            <div style={s.ratingBadge}><FontAwesomeIcon icon={faStar} /> {user.rating_score}</div>
                        </div>
                        <h2 style={s.nameText}>{user.full_name}</h2>
                        <span style={s.roleLabel}>{user.role === 'worker' ? t('role_worker') : t('role_customer')}</span>

                        <div style={s.balanceWrapper}>
                            <FontAwesomeIcon icon={faWallet} style={{marginRight: 8, color: gold}} />
                            <span>{user.balance?.toLocaleString()} ֏</span>
                        </div>

                        <div style={s.navTabs}>
                            {['orders', 'info', 'settings'].map((tab) => (
                                <div 
                                    key={tab}
                                    onClick={() => setActiveTab(tab)} 
                                    style={{
                                        ...s.tabItem, 
                                        borderBottom: activeTab === tab ? `2px solid ${gold}` : 'none', 
                                        color: activeTab === tab ? gold : '#aaa'
                                    }}
                                >
                                    {t(`tab_${tab}`)}
                                </div>
                            ))}
                        </div>
                    </div>

                    {activeTab === 'orders' && (
                        <div style={s.tabContent}>
                            <div style={s.sectionTitle}>{t('recent_orders')}</div>
                            {loadingOrders ? (
                                <div style={s.emptyMsg}><FontAwesomeIcon icon={faSpinner} spin /> {t('loading')}</div>
                            ) : orders.length > 0 ? (
                                orders.slice(0, 3).map(order => (
                                    <div key={order.id} style={s.miniOrderCard}>
                                        <div style={s.orderIcon}><FontAwesomeIcon icon={faListAlt} /></div>
                                        <div style={{flex:1}}>
                                            <div style={{fontSize:'13px', fontWeight:'bold', color: '#fff'}}>{t('order_id')} #{order.id}</div>
                                            <div style={{fontSize:'11px', color:'#888'}}>{order.service_name || '...'}</div>
                                        </div>
                                        <div style={{fontSize:'10px', color: getStatusColor(order.status), fontWeight:'bold', textTransform: 'uppercase'}}>
                                            {order.status}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={s.emptyMsg}>{t('no_orders')}</div>
                            )}
                            <button onClick={() => navigate('/my-orders')} style={s.seeAllBtn}>
                                {t('see_all')} <FontAwesomeIcon icon={faChevronRight} style={{marginLeft: 10, fontSize: 10}} />
                            </button>
                        </div>
                    )}

                    {activeTab === 'info' && (
                        <div style={s.tabContent}>
                            <div style={s.inputGroup}>
                                <label style={s.label}>{t('full_name')}</label>
                                <input style={s.input} value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                            </div>
                            <div style={s.inputGroup}>
                                <label style={s.label}>{t('email')}</label>
                                <input style={s.input} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                            </div>
                            <div style={s.inputGroup}>
                                <label style={s.label}>{t('phone_1')}</label>
                                <input style={s.input} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                            </div>
                            <div style={s.inputGroup}>
                                <label style={s.label}>{t('phone_2')}</label>
                                <input style={s.input} value={formData.phone1} onChange={e => setFormData({...formData, phone1: e.target.value})} />
                            </div>
                            <div style={s.inputGroup}>
                                <label style={s.label}>{t('address')}</label>
                                <input style={s.input} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
                            </div>
                            <button onClick={handleUpdateInfo} style={s.saveBtn} disabled={loading}>
                                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : t('save_changes')}
                            </button>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div style={s.tabContent}>
                            <div style={s.sectionTitle}>{t('notifications')}</div>
                            <div style={s.settingRow}>
                                <span style={{color: '#fff'}}>{t('email_notify')}</span>
                                <Toggle active={user.notification_settings?.email} onToggle={() => updateNotify('email', !user.notification_settings?.email)} />
                            </div>
                            <div style={s.settingRow}>
                                <span style={{color: '#fff'}}>{t('sms_notify')}</span>
                                <Toggle active={user.notification_settings?.sms} onToggle={() => updateNotify('sms', !user.notification_settings?.sms)} />
                            </div>
                            <div style={s.regDate}>
                                <FontAwesomeIcon icon={faCalendarAlt} /> {t('reg_date')}՝ {new Date(user.created_at).toLocaleDateString()}
                            </div>
                        </div>
                    )}
                </div>

                <div style={s.footer}>
                    <button onClick={onLogout} style={s.logoutBtn}>{t('logout')}</button>
                    {isMobile && <div style={{ height: '80px', width: '100%' }}></div>}
                </div>
            </div>
        </div>
    );
};




const Toggle = ({ active, onToggle }) => (
    <div onClick={onToggle} style={{...s.toggleTrack, background: active ? '#c5a059' : '#333'}}>
        <div style={{...s.toggleCircle, transform: active ? 'translateX(16px)' : 'translateX(0px)'}} />
    </div>
);

const s = {
    desktopOverlay: { 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100dvh', 
        background: 'rgba(10,10,10,0.9)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 2000, 
        backdropFilter: 'blur(15px)' 
    },
    desktopModal: { 
        width: '420px', 
        height: '85vh', // Բարձրությունը ֆիքսված է
        background: '#1a1a1a', 
        borderRadius: '30px', 
        display: 'flex', 
        flexDirection: 'column', 
        border: '1px solid #333',
        overflow: 'hidden' // Մոդալը ինքը չի սքրոլվում
    },
    fullWrapper: { 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100dvh', 
        background: '#1a1a1a', 
        zIndex: 2000, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden' 
    },
    mobileContainer: { 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
    },
    header: { 
        padding: '20px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: '1px solid #2a2a2a',
        flexShrink: 0 // Չի սեղմվում
    },
    headerTitle: { fontSize: '11px', letterSpacing: '3px', fontWeight: 'bold', color: '#888' },
    backBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer' },
    
    scrollArea: { 
        flex: 1, // Վերցնում է ամբողջ ազատ տարածքը
        overflowY: 'auto', // ԱՅՍՏԵՂ Է ՍՔՐՈԼԸ
        padding: '0 25px 30px',
        WebkitOverflowScrolling: 'touch'
    },
    
    hero: { textAlign: 'center', padding: '20px 0' },
    avatarWrapper: { position: 'relative', width: '110px', height: '110px', margin: '0 auto 15px' },
    avatar: { width: '100%', height: '100%', borderRadius: '40px', objectFit: 'cover', border: '2px solid #c5a059', padding: '4px' },
    photoEditBtn: { position: 'absolute', bottom: 0, right: 0, background: '#c5a059', width: '32px', height: '32px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', border: '4px solid #1a1a1a', cursor: 'pointer' },
    ratingBadge: { position: 'absolute', top: -5, left: -5, background: '#c5a059', color: '#000', padding: '3px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold' },
    nameText: { fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px', color: '#fff' },
    roleLabel: { fontSize: '9px', background: '#2a2a2a', color: '#c5a059', padding: '4px 15px', borderRadius: '20px', fontWeight: 'bold', border: '1px solid #333' },
    balanceWrapper: { marginTop: '12px', fontSize: '18px', fontWeight: 'bold', color: '#fff' },
    
    navTabs: { 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: '25px', 
        borderBottom: '1px solid #2a2a2a',
        position: 'sticky',
        top: 0,
        background: '#1a1a1a', // Որ սքրոլի ժամանակ տակից տեքստը չերևա
        zIndex: 10
    },
    tabItem: { flex: 1, padding: '12px 0', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', transition: '0.2s' },
    tabContent: { paddingBottom: '20px' },
    
    miniOrderCard: { background: '#252525', padding: '14px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px', border: '1px solid #333' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' },
    input: { background: '#252525', border: '1px solid #333', borderRadius: '14px', padding: '14px', color: '#fff', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' },
    saveBtn: { width: '100%', background: '#c5a059', color: '#000', border: 'none', padding: '18px', borderRadius: '18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', marginTop: '10px' },
    
    footer: { 
        padding: '15px 25px', 
        borderTop: '1px solid #2a2a2a',
        background: '#1a1a1a',
        flexShrink: 0, // Չի թողնում footer-ը կորի
        boxSizing: 'border-box'
    },
    logoutBtn: { 
        width: '100%', 
        padding: '16px', 
        background: 'rgba(255, 68, 68, 0.1)', 
        border: '1px solid #ff4444', 
        color: '#ff4444', 
        borderRadius: '18px', 
        fontWeight: 'bold', 
        cursor: 'pointer' 
    },
    settingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', background: '#252525', borderRadius: '18px', marginBottom: '10px', border: '1px solid #333' },
    toggleTrack: { width: '38px', height: '22px', borderRadius: '12px', padding: '3px', cursor: 'pointer' },
    toggleCircle: { width: '16px', height: '16px', background: '#fff', borderRadius: '50%', transition: '0.3s' },
    label: { fontSize: '9px', color: '#888', fontWeight: 'bold', marginLeft: '5px' },
    sectionTitle: { fontSize: '10px', color: '#c5a059', fontWeight: 'bold', marginBottom: '15px', letterSpacing: '1px' },
    seeAllBtn: { width: '100%', padding: '16px', background: 'none', border: '1px solid #333', color: '#fff', borderRadius: '18px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' },
    emptyMsg: { textAlign: 'center', color: '#555', fontSize: '13px', padding: '20px' },
    regDate: { textAlign: 'center', fontSize: '11px', color: '#555', marginTop: '25px' },
    orderIcon: { width: '38px', height: '38px', background: '#333', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c5a059' },
};

export default Profile;